const cleanups = new Map<HTMLElement, () => void>();

export function teardownDocumentPreviewPolling() {
  for (const cleanup of cleanups.values()) cleanup();
  cleanups.clear();
}

export function initDocumentPreviewPolling() {
  document.querySelectorAll<HTMLElement>("[data-document-preview-poll]").forEach((element) => {
    if (cleanups.has(element)) return;
    const endpoint = new URL(element.dataset.documentPreviewPoll!, window.location.href);
    if (endpoint.origin !== window.location.origin) return;
    let stopped = false;
    let attempts = 0;
    let errors = 0;
    let timer: ReturnType<typeof setTimeout>;
    let request: AbortController | undefined;
    const stop = () => {
      stopped = true;
      clearTimeout(timer);
      request?.abort();
    };
    cleanups.set(element, stop);
    const message = (text: string) => {
      const progress = element.querySelector("[data-preview-progress]");
      if (progress) progress.textContent = text;
      stop();
    };
    const poll = async () => {
      if (stopped || !element.isConnected) return stop();
      request = new AbortController();
      const timeout = setTimeout(() => request?.abort(), 10000);
      attempts += 1;
      try {
        const response = await fetch(endpoint.href, {
          credentials: "same-origin", cache: "no-store", redirect: "error",
          headers: { Accept: "application/json" }, signal: request.signal,
        });
        if ([401, 403, 404].includes(response.status)) {
          return message("Preview access has changed. Refresh the page to continue.");
        }
        if (!response.ok) throw new Error("Preview status unavailable");
        const result = await response.json();
        if (stopped || !element.isConnected) return;
        if (result.status === "completed" && typeof result.url === "string") {
          const url = new URL(result.url, window.location.href);
          if (url.origin !== window.location.origin) throw new Error("Invalid preview URL");
          url.hash = element.dataset.previewFragment || "";
          const frame = document.createElement("iframe");
          frame.src = url.href;
          frame.title = element.dataset.previewTitle || "Document preview";
          frame.className = `${element.dataset.previewHeight || "h-[80vh]"} w-full bg-white`;
          frame.dataset.documentCompareFrame = element.dataset.previewCompare || "false";
          const wrapper = document.createElement("div");
          wrapper.className = "overflow-hidden rounded-2xl border border-neutral-200/80 bg-white dark:border-neutral-800";
          if (element.classList.contains("mt-5")) wrapper.classList.add("mt-5");
          wrapper.append(frame);
          element.replaceWith(wrapper);
          return stop();
        }
        if (result.status === "failed") {
          const heading = element.querySelector(".font-medium");
          if (heading) heading.textContent = "PDF preview could not be generated for this office document.";
          return message("Use Download to open the original file.");
        }
        if (!["requested", "processing"].includes(result.status)) {
          return message("A current preview is unavailable. Refresh the page to try again, or use Download.");
        }
        errors = 0;
      } catch {
        if (stopped) return;
        errors += 1;
        if (errors >= 3) return message("Automatic preview checks could not connect. Refresh the page to try again.");
      } finally {
        clearTimeout(timeout);
      }
      if (attempts >= 60) return message("The preview is taking longer than expected. Refresh to check again, or use Download.");
      if (!stopped) timer = setTimeout(poll, 5000);
    };
    timer = setTimeout(poll, 5000);
  });
}
